import type {ReactElement} from 'react';
import {useEffect, useRef, useState} from 'react';
import useDidUpdateEffect from '@Hooks/useDidUpdateEffect';
import {noop} from '@Utils/general.util';
import type {PosterPageData} from '@Libraries/poster-library';
import {useContainerQuery} from '@Hooks/useContainerQuery';
import {DesignGallery} from '@Components/design-gallery';
import {selectTab} from '@Components/tabs/tabs-reducer';
import type {DesignSelectorProps, LimitExceededAction} from '@Components/design-selector/design-selector.types';
import type {FolderGridBand} from '@Components/design-selector/design-selector.types';
import {
  containerQuery,
  DESIGN_SELECTOR_ELEMENT_DESKTOP_CLASS,
  DESIGN_SELECTOR_ELEMENT_WIZARD_CLASS,
  DesignSelectorFrameVariant,
  DesignSelectorTab,
  FOLDER_GRID_CONTAINER_MEASURED_QUERY_KEY,
  folderGridContainerQuery,
  getActiveFolderGridBand,
  getDesignSelectorTopTabKey,
  NO_PRESELECTED_DESIGNS,
} from '@Components/design-selector/design-selector.types';
import {GalleryThemeURI} from '@Components/design-gallery/design-gallery.types';
import {DesignSelectorSkeletonLoader} from './components/design-selector-skeleton-loader/design-selector-skeleton-loader';
import {DesignSelectorTabSwitcher} from './components/design-selector-tab-switcher/design-selector-tab-switcher';
import {BrandTemplatesTab} from './components/brand-templates-tab/brand-templates-tab';
import styles from './design-selector.module.scss';
import {useAppDispatch} from '@/hooks';
import '@Global/create-from-scratch';
import '@Global/toast';
import type {DesignSelection} from '@Components/social-media/social-media.types';

const LOAD_POSTERS_IF_NOT_LOADED_CUSTOM_EVENT = 'loadDesignsForDesignSelectorIfNotLoaded';
const DESIGN_SELECTOR_ELEMENT_TABS_ID = 'design-selector-element-tabs';

function toDesignSelection(entry: PosterPageData): DesignSelection {
  return {posterHashedId: entry.posterId, pageHashedId: entry.pageId};
}

export function DesignSelector({
  onDesignsSelection = noop,
  initialDesignSelections = [],
  containerRef,
  onGalleryTemplateSelectionCallback = noop,
  onTabSelected = noop,
  designSelectorTab = DesignSelectorTab.MY_DESIGNS,
  startDesignFromMediaModalBtnOpts,
  onMyDesignsCreateWithAiClick,
  onGalleryCreateWithAiClick,
  isLoading = false,
  designGalleryStartingURI = GalleryThemeURI.DEFAULT,
  hideTabContainer = false,
  frameVariant = DesignSelectorFrameVariant.DEFAULT,
  desktopClasses = '',
  mobileClasses = '',
  designGalleryHeight = '80vh',
  maxSelection,
  onLimitExceeded,
  scrollContainerEl = null,
  selectedDesignCount,
  onDeselectAll,
  doSelectPosterOnly = false,
  brandTemplateSelections = NO_PRESELECTED_DESIGNS,
  onBrandTemplatesSelection = noop,
  brandTemplatesExcludedBrandId,
}: DesignSelectorProps): ReactElement {
  const params = useContainerQuery(containerRef, containerQuery);
  const designSelectorElementRef = useRef<HTMLDivElement>(null);
  const folderGridParams = useContainerQuery(designSelectorElementRef, folderGridContainerQuery);
  const lastMeasuredFolderGridBandRef = useRef<FolderGridBand | null>(null);

  if (folderGridParams[FOLDER_GRID_CONTAINER_MEASURED_QUERY_KEY]) {
    lastMeasuredFolderGridBandRef.current = getActiveFolderGridBand(folderGridParams);
  }

  const folderGridBand = lastMeasuredFolderGridBandRef.current;
  const [areMyDesignsLoaded, setAreMyDesignsLoaded] = useState<boolean>(false);
  const [isGalleryMounted, setIsGalleryMounted] = useState<boolean>(designSelectorTab === DesignSelectorTab.GALLERY_TEMPLATES);
  const [myDesignsLength, setMyDesignsLength] = useState<number>(0);
  const [areSharedWithMeDesignsLoaded, setAreSharedWithMeDesignsLoaded] = useState(false);
  const [hasBrandTemplatesTabOpened, setHasBrandTemplatesTabOpened] = useState(false);
  const dispatch = useAppDispatch();

  const isMyDesignsSelected = (): boolean => {
    return designSelectorTab === DesignSelectorTab.MY_DESIGNS;
  };

  const isTeamSelected = (): boolean => {
    return designSelectorTab === DesignSelectorTab.SHARED_WITH_ME;
  };

  const isDesignGallerySelected = (): boolean => {
    return designSelectorTab === DesignSelectorTab.GALLERY_TEMPLATES;
  };

  const isBrandTemplatesSelected = (): boolean => {
    return designSelectorTab === DesignSelectorTab.BRAND_TEMPLATES;
  };

  const shouldShowLoader = (): boolean => {
    if (isDesignGallerySelected() || isBrandTemplatesSelected()) {
      return false;
    }
    if (isMyDesignsSelected() && !areMyDesignsLoaded) {
      return true;
    }
    return isTeamSelected() && !areSharedWithMeDesignsLoaded;
  };

  const getMyDesignsLength = (): number => {
    return containerRef.current?.querySelectorAll('.js-poster[data-id]').length ?? 0;
  };

  const onDesignsSelectionRef = useRef(onDesignsSelection);
  const onLimitExceededRef = useRef(onLimitExceeded);
  const maxSelectionRef = useRef(maxSelection);
  const startDesignFromMediaModalBtnOptsRef = useRef(startDesignFromMediaModalBtnOpts);
  const onMyDesignsCreateWithAiClickRef = useRef(onMyDesignsCreateWithAiClick);

  useEffect(() => {
    onDesignsSelectionRef.current = onDesignsSelection;
  });
  useEffect(() => {
    onLimitExceededRef.current = onLimitExceeded;
  });
  useEffect(() => {
    maxSelectionRef.current = maxSelection;
  });
  useEffect(() => {
    startDesignFromMediaModalBtnOptsRef.current = startDesignFromMediaModalBtnOpts;
  });
  useEffect(() => {
    onMyDesignsCreateWithAiClickRef.current = onMyDesignsCreateWithAiClick;
  });

  const pendingSelectionRef = useRef<(PosterPageData | DesignSelection)[] | null>(null);

  const openStartDesignFromUploadModal = (): void => {
    const uploadModalOpts = startDesignFromMediaModalBtnOptsRef.current;

    if (uploadModalOpts) {
      window.PMW.triggerCreateDesignFromUserUpload(uploadModalOpts);
    }
  };

  const callMyDesignsCreateWithAiClick = (): void => {
    onMyDesignsCreateWithAiClickRef.current?.();
  };

  useEffect(() => {
    let dispose: VoidFunction | undefined;
    let cancelled = false;

    window.PMW.util.require(typeof window.PMW.designSelector === 'undefined', 'designSelector', true, () => {
      if (cancelled) {
        return;
      }
      const initialLimitExceededHandler = onLimitExceededRef.current;
      dispose = window.PMW.designSelector.openEmbedded({
        divToLoadIn: containerRef.current?.querySelector('.js-design-selector-element') ?? '.js-design-selector-element',
        scrollContainerEl: scrollContainerEl ?? containerRef.current,
        loadPostersIfNotLoadedEventType: LOAD_POSTERS_IF_NOT_LOADED_CUSTOM_EVENT,
        preselectedDesigns: initialDesignSelections,
        isMultiSelectionAllowed: maxSelectionRef.current !== 1,
        doSelectPosterOnly,
        addCheckmarkOnSelection: true,
        selectedNameClass: styles.selectedName,
        maxSelection: maxSelectionRef.current,
        onLimitExceeded: initialLimitExceededHandler
          ? (info): LimitExceededAction => {
              return (onLimitExceededRef.current ?? initialLimitExceededHandler)({
                currentSelection: info.currentSelection.map(toDesignSelection),
                tentativeSelection: info.tentativeSelection.map(toDesignSelection),
                newlyAdded: info.newlyAdded.map(toDesignSelection),
                maxSelection: info.maxSelection,
                isBulkAdd: info.isBulkAdd,
              });
            }
          : undefined,
        startFromMediaModalBtnOpts: startDesignFromMediaModalBtnOptsRef.current,
        onCreateWithAiClick: onMyDesignsCreateWithAiClickRef.current ? callMyDesignsCreateWithAiClick : undefined,
        onSelectionChange: (selection): void => {
          onDesignsSelectionRef.current(selection.map(toDesignSelection));
        },
        onLoaded: (): void => {
          setAreMyDesignsLoaded(true);
          if (pendingSelectionRef.current !== null) {
            window.PMW.designSelector.setSelection(pendingSelectionRef.current);
            pendingSelectionRef.current = null;
          }
        },
      });
    });

    return (): void => {
      cancelled = true;
      dispose?.();
    };
  }, []);

  useEffect(() => {
    if (!areMyDesignsLoaded) {
      return;
    }
    const raf = requestAnimationFrame(() => {
      setMyDesignsLength(getMyDesignsLength());
    });

    return (): void => {
      cancelAnimationFrame(raf);
    };
  }, [areMyDesignsLoaded]);

  useEffect(() => {
    if (!folderGridBand || typeof window.PMW.designSelector === 'undefined') {
      return;
    }

    if (!areMyDesignsLoaded && !areSharedWithMeDesignsLoaded) {
      return;
    }

    window.PMW.designSelector.refreshFolderCollapse();
  }, [folderGridBand, areMyDesignsLoaded, areSharedWithMeDesignsLoaded, designSelectorTab]);

  useDidUpdateEffect(() => {
    if (typeof window.PMW.designSelector === 'undefined') {
      pendingSelectionRef.current = initialDesignSelections;
      return;
    }
    window.PMW.designSelector.setSelection(initialDesignSelections);
  }, [initialDesignSelections]);

  useEffect(() => {
    dispatch(selectTab(DESIGN_SELECTOR_ELEMENT_TABS_ID, getDesignSelectorTopTabKey(designSelectorTab)));

    if (designSelectorTab === DesignSelectorTab.GALLERY_TEMPLATES) {
      setIsGalleryMounted(true);
    }

    if (isBrandTemplatesSelected()) {
      setHasBrandTemplatesTabOpened(true);
    }
  }, [designSelectorTab]);

  useDidUpdateEffect(() => {
    if (!shouldShowLoader()) {
      return;
    }

    let cancelled = false;

    window.dispatchEvent(
      new CustomEvent(LOAD_POSTERS_IF_NOT_LOADED_CUSTOM_EVENT, {
        detail: {
          onDesignLoadedCallback: (): void => {
            if (cancelled) {
              return;
            }
            if (isMyDesignsSelected()) {
              setAreMyDesignsLoaded(true);
            } else if (isTeamSelected()) {
              setAreSharedWithMeDesignsLoaded(true);
            }
          },
        },
      })
    );

    return (): void => {
      cancelled = true;
    };
  }, [designSelectorTab]);

  const renderGalleryTemplateSelector = (): ReactElement | null => {
    if (!isGalleryMounted) {
      return null;
    }

    return (
      <div className={getGalleryTemplateSelectorClasses()}>
        <DesignGallery
          disableCreateFromScratchCTA
          onDesignsSelected={onGalleryTemplateSelectionCallback}
          onUploadMediaClick={startDesignFromMediaModalBtnOpts ? openStartDesignFromUploadModal : undefined}
          onCreateWithAiClick={onGalleryCreateWithAiClick}
          height={designGalleryHeight}
          startingPageURI={designGalleryStartingURI}
        />
      </div>
    );
  };

  const getDesignSelectorLoadingClass = (): string => {
    if (isLoading) {
      return styles.isLoadingDesignSelector;
    }

    return '';
  };

  const isSocialMediaWizardVariant = (): boolean => {
    return frameVariant === DesignSelectorFrameVariant.SOCIAL_MEDIA_WIZARD;
  };

  const getDesignSelectorDesktopClasses = (): string => {
    const frameClass = isSocialMediaWizardVariant() ? styles.designSelectorDesktopSocialMediaWizard : styles.designSelectorDesktop;
    return `${frameClass} ${desktopClasses}`;
  };

  const getDesignSelectorMobileClasses = (): string => {
    return `${styles.designSelectorMobile} ${mobileClasses}`;
  };

  const isMobileLayout = (): boolean => {
    return params.small && !isSocialMediaWizardVariant();
  };

  const getDesignSelectorClasses = (): string => {
    let classes = getDesignSelectorLoadingClass();

    if (isMobileLayout()) {
      classes += ` ${getDesignSelectorMobileClasses()}`;
    } else {
      classes += ` ${getDesignSelectorDesktopClasses()}`;
    }

    if (isDesignGallerySelected()) {
      classes += ` ${styles.templateGallerySelector}`;
    }

    return classes;
  };
  const getContainerScopeClasses = (): string => {
    const breakpointClass = isMobileLayout() ? '' : DESIGN_SELECTOR_ELEMENT_DESKTOP_CLASS;
    const variantClass = isSocialMediaWizardVariant() ? DESIGN_SELECTOR_ELEMENT_WIZARD_CLASS : '';
    return `${breakpointClass} ${variantClass} ${folderGridBand?.scopeClass ?? ''}`;
  };

  const getDesignSelectorElementContentClasses = (): string => {
    const visibility = isMyDesignsSelected() || isTeamSelected() ? '' : '_hidden';
    return `js-design-selector-element design-selector-element ${styles.designSelectorElementContent} ${visibility} ${getContainerScopeClasses()}`;
  };

  const getMystuffPostersClasses = (): string => {
    const visibility = isMyDesignsSelected() ? 'selected' : '_hidden';
    const emptyState = myDesignsLength ? '' : styles.emptyMyDesigns;
    return `mystuff-posters ${visibility} ${emptyState}`;
  };

  const getSharedWithMePostersClasses = (): string => {
    return `shared-with-me-posters ${isTeamSelected() ? 'selected' : '_hidden'}`;
  };

  const getGalleryTemplateSelectorClasses = (): string => {
    return `${styles.galleryTemplateSelector} ${isDesignGallerySelected() ? '' : '_hidden'}`;
  };

  /**
   * Mounted on first open, since it loads every kit and their templates, which no other tab needs. It then
   * stays mounted while hidden, so the kit picked in it is still shown when the user comes back.
   */
  const getBrandTemplatesTab = (): ReactElement | null => {
    const isShown = isBrandTemplatesSelected();

    if (!hasBrandTemplatesTabOpened && !isShown) {
      return null;
    }

    return (
      <div className={isShown ? '' : '_hidden'}>
        <BrandTemplatesTab
          isShown={isShown}
          selectedDesigns={brandTemplateSelections}
          onSelectionChange={onBrandTemplatesSelection}
          excludedBrandId={brandTemplatesExcludedBrandId}
          onAddBrandTemplates={() => {
            onTabSelected(DesignSelectorTab.MY_DESIGNS);
          }}
        />
      </div>
    );
  };

  const getDesignSelector = (): ReactElement => {
    return (
      <div className={getDesignSelectorClasses()}>
        <DesignSelectorTabSwitcher
          designSelectorTab={designSelectorTab}
          onTabSelected={onTabSelected}
          isMobile={isMobileLayout()}
          hideTabContainer={hideTabContainer}
          tabsId={DESIGN_SELECTOR_ELEMENT_TABS_ID}
          selectedDesignCount={selectedDesignCount}
          onDeselectAll={onDeselectAll}
        />
        <DesignSelectorSkeletonLoader isVisible={shouldShowLoader()} containerRef={containerRef} folderGridBand={folderGridBand} isDesktopVersion={!isMobileLayout()} />
        <div ref={designSelectorElementRef} className={getDesignSelectorElementContentClasses()}>
          <div className={getMystuffPostersClasses()} />
          <div className={getSharedWithMePostersClasses()} />
        </div>

        {renderGalleryTemplateSelector()}
        {getBrandTemplatesTab()}
      </div>
    );
  };

  return <div ref={containerRef}>{getDesignSelector()}</div>;
}
